import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SignaturePad from "@/components/SignaturePad";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const FORM_META = {
  nda: { label: "Non-Disclosure Agreement (NDA)", body: "I agree to keep all Guild trustee communications, case strategy, and ledger findings confidential. Disclosure beyond the Guild requires written trustee approval." },
  non_disclosure: { label: "Trustee Non-Disclosure Form", body: "I acknowledge that ledger audits and pledge filings prepared by Azure Gypsy Guild trustees are protected work product and will not be shared outside the Guild." },
  membership: { label: "Service Membership Agreement", body: "I accept the yearly Guild membership terms, the 1.5% counterclaim service fee on my mortgage balance (paid only at settlement), and the trustee service schedule. Membership is renewed annually." },
  moral_clause: { label: "Moral Clause Agreement", body: "I commit to ethical conduct during the counterclaim process, to truthful disclosure on my application, and to cooperation with the trustees and mediators." },
  civil_rights: { label: "Civil Rights Violations Disclosure", body: "I authorize Azure Gypsy Guild to record, investigate, and pursue civil rights violations that may have occurred during foreclosure, taxation, or lending actions against me." },
  mediation: { label: "Foreclosure Mediation & Arbitration Agreement", body: "I consent to Guild-led foreclosure mediation, and to binding arbitration if mediation does not produce settlement within the 60-day window." },
};

export default function ESign() {
  const { formType } = useParams();
  const navigate = useNavigate();
  const meta = FORM_META[formType];
  const [signature, setSignature] = useState("");
  const [typedName, setTypedName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    api.get("/esign/me").then((r) => {
      const found = r.data.find((s) => s.form_type === formType);
      setExisting(found || null);
    }).catch(() => {});
  }, [formType]);

  if (!meta) {
    return (
      <div className="min-h-screen bg-paper">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 py-16">
          <p data-testid="esign-unknown-form">Unknown form: {formType}</p>
          <Button onClick={() => navigate("/portal")} className="mt-4">Back to portal</Button>
        </main>
        <Footer />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!signature) { toast.error("Please draw your signature"); return; }
    if (!typedName.trim()) { toast.error("Type your full name"); return; }
    if (!agreed) { toast.error("You must check the agreement box"); return; }
    setBusy(true);
    try {
      await api.post("/esign", {
        form_type: formType,
        signature_data_url: signature,
        typed_name: typedName,
        agreed: true,
      });
      toast.success("Signature recorded. Trustees notified.");
      navigate("/portal");
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="esign-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Card className="border-black/5">
          <CardHeader>
            <span className="uppercase text-xs tracking-[0.25em] text-[color:var(--fire-red)]">E-Signature</span>
            <CardTitle className="font-serif text-3xl mt-2" data-testid="esign-title">{meta.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md bg-[color:var(--bg-secondary)] p-5 text-[15px] leading-relaxed">
              {meta.body}
            </div>

            {existing ? (
              <div className="mt-6 border border-[color:var(--bright-green)]/40 bg-[color:var(--bright-green)]/5 rounded-md p-4" data-testid="esign-existing">
                <p className="text-sm">
                  Signed by <strong>{existing.typed_name}</strong> on {new Date(existing.signed_at).toLocaleString()} from IP {existing.ip}.
                </p>
                <Button onClick={() => navigate("/portal")} className="mt-3" variant="outline">Back to portal</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-5">
                <div>
                  <Label htmlFor="typed_name">Type your full legal name</Label>
                  <Input id="typed_name" value={typedName} onChange={(e) => setTypedName(e.target.value)} required data-testid="esign-typed-name" />
                </div>
                <SignaturePad onChange={setSignature} />
                <label className="flex items-start gap-3 text-sm" data-testid="esign-agree-row">
                  <Checkbox checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" />
                  <span>I have read and agree to the above. My signature, typed name, IP address, and timestamp will be permanently recorded.</span>
                </label>
                <Button type="submit" disabled={busy} className="w-full bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" data-testid="esign-submit-btn">
                  {busy ? "Recording…" : "Sign & submit"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
